/**
 * Whether an auction's lots fit before bidding on them closes (#171), kept pure so it can be tested
 * on its own. Create Auction (Super-admin/CreateAuction.js) and the Update Auction modal
 * (Super-admin/Auction.js) show it under Bid Time, and the auction list counts the lots that did
 * not get their turn.
 *
 * It mirrors usp_CRUDAuction's fit check (#190): the lots run one after another, each for at least
 * the bid time, and bidding closes 15 minutes before the contest starts (dbo.fn_BidWindow), so an
 * auction whose teams x bid time is longer than the time from its start to that moment is refused.
 * That is a floor, not a guarantee: every bid restarts a lot's countdown, so a busy auction runs
 * longer, and the lots still waiting when bidding closes are closed without ever opening. The
 * forms said none of this, and the list did not show the lots it happened to.
 */

import { parseServerUtc } from './lotClock';

/** Bidding on an auction's lots closes this long before its contest starts (dbo.fn_BidWindow). */
export const BID_WINDOW_MS = 15 * 60 * 1000;

const MINUTE_MS = 60 * 1000;

/**
 * The lots a teamId list makes, counted as the server counts them: every id that reads as a whole
 * number. The Field is one of them when it is in the list.
 *
 * @param {Array} teamIds The ids the form will send.
 * @returns {number}
 */
export const countLots = (teamIds) =>
    (Array.isArray(teamIds) ? teamIds : []).filter((id) => /^[+-]?\d+$/.test(String(id ?? '').trim()))
        .length;

/**
 * Reads a date the forms hold: a string from the API (UTC, usually without a zone), a Date from the
 * date picker, or a moment.
 *
 * @param {*} value The date.
 * @returns {number} Epoch milliseconds, or NaN when there is none.
 */
export const readInstant = (value) => {
    if (value === null || value === undefined || value === '') return NaN;
    if (typeof value === 'string') return parseServerUtc(value);
    if (value instanceof Date) return value.getTime();
    const ms = Number(value);
    return Number.isFinite(ms) ? ms : NaN;
};

/** Minutes as a short figure: 14, 3.5, 2.25. */
const formatMinutes = (minutes) => String(Math.round(minutes * 100) / 100);

/**
 * A lot's bid time as a short figure: under a minute in seconds, as the forms label it ("45 sec"),
 * otherwise in minutes ("2 min").
 */
const formatBidTime = (minutes) =>
    minutes < 1 ? `${Math.round(minutes * 60)} sec` : `${formatMinutes(minutes)} min`;

/**
 * The fit of an auction's lots, and the sentence the forms show under Bid Time.
 *
 * @param {{teamIds: Array, bidTime: *, auctionStart: *, contestStart: *}} input The ids to be sent,
 *   the bid time in minutes, the auction's start and the contest's start (see readInstant).
 * @returns {{lots: number, minimumMinutes: number, availableMinutes: number|null,
 *   fits: boolean|null, text: string}|null} null until there are teams and a bid time. Without
 *   both dates availableMinutes and fits are null and the text gives only the minimum. Available
 *   time is rounded down to a tenth of a minute and never shown below 0; fits compares exactly.
 */
export const describeAuctionFit = ({ teamIds, bidTime, auctionStart, contestStart }) => {
    const lots = countLots(teamIds);
    const minutesPerLot = Number(bidTime);
    if (lots === 0 || !(minutesPerLot > 0)) return null;

    const minimumMinutes = lots * minutesPerLot;
    const start = readInstant(auctionStart);
    const contest = readInstant(contestStart);
    const availableMs =
        Number.isFinite(start) && Number.isFinite(contest) ? contest - BID_WINDOW_MS - start : null;
    const availableMinutes =
        availableMs === null ? null : Math.max(0, Math.floor(availableMs / (MINUTE_MS / 10)) / 10);
    const fits = availableMs === null ? null : minimumMinutes * MINUTE_MS <= availableMs;

    const need = `${lots} ${lots === 1 ? 'team' : 'teams'} × ${formatBidTime(minutesPerLot)} = ${formatMinutes(minimumMinutes)} min minimum`;
    const closing = 'before bidding closes (15 min before the contest starts)';
    const why = "Every bid restarts a lot's countdown, so lots still waiting when bidding closes are closed unsold.";
    const text =
        availableMinutes === null
            ? `${need} ${closing}. ${why}`
            : `${need}; ${formatMinutes(availableMinutes)} min available ${closing}. ${why}`;

    return { lots, minimumMinutes, availableMinutes, fits, text };
};

/**
 * The auction list's note for the primary lots that bidding closed before they ever opened
 * (lotsClosedUnopened, #171).
 *
 * @param {*} count The row's lotsClosedUnopened; an older API sends none.
 * @returns {string|null} e.g. "2 lots closed unopened when bidding closed", null for none.
 */
export const describeClosedUnopened = (count) => {
    const lots = Number(count);
    if (!Number.isInteger(lots) || lots <= 0) return null;
    return `${lots} ${lots === 1 ? 'lot' : 'lots'} closed unopened when bidding closed`;
};
